import { useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/useAuth";
import type { Rating } from "../lib/types";

function prettyGame(id: string): string {
  return id.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function Stats({ ratings }: { ratings: Rating[] | null }) {
  if (!Array.isArray(ratings)) return <p className="acct-stats-msg">Loading your stats…</p>;
  if (!ratings.length) return <p className="acct-stats-msg">No ranked games yet — play some multiplayer!</p>;
  const top = [...ratings].sort((a, b) => (b.rating || 0) - (a.rating || 0)).slice(0, 5);
  return (
    <>
      <p className="acct-stats-head">Your ratings</p>
      <ul className="acct-stats-list">
        {top.map((r) => (
          <li className="acct-stats-row" key={r.game}>
            <span className="acct-stats-game" title={prettyGame(r.game)}>
              {prettyGame(r.game)}
            </span>
            <span className="acct-stats-rating">{r.rating ?? "—"}</span>
            <span
              className="acct-stats-record"
              title={`${r.wins || 0}W · ${r.losses || 0}L · ${r.games || 0} games`}
            >
              {r.wins || 0}–{r.losses || 0}
            </span>
          </li>
        ))}
      </ul>
    </>
  );
}

// Masthead account control: a "Sign in" button when signed out, or a chip that
// opens an inline disclosure (ratings + leaderboards + sign out) when signed in.
export function AccountMenu() {
  const { account, ratings, refreshMe, signOut, openAuth } = useAuth();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    void refreshMe();
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("click", onClick, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open, refreshMe]);

  if (!account) {
    return (
      <button className="acct-btn" type="button" onClick={() => openAuth("signin")}>
        Sign in
      </button>
    );
  }

  const monogram = (account.username[0] || "?").toUpperCase();
  return (
    <div className="acct" ref={wrapRef}>
      <button
        className="acct-chip"
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls="acct-menu"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="acct-avatar" aria-hidden="true">
          {monogram}
        </span>
        <span className="acct-name">{account.username}</span>
        <span className="acct-caret" aria-hidden="true" />
      </button>
      <div className="acct-menu" id="acct-menu" hidden={!open}>
        <p className="acct-menu-head">
          Signed in as <strong>{account.username}</strong>
        </p>
        <div className="acct-stats">
          <Stats ratings={ratings} />
        </div>
        <a className="acct-menu-item" href="/leaderboard" target="_blank" rel="noreferrer">
          Full leaderboards ↗
        </a>
        <button
          className="acct-menu-item acct-menu-signout"
          type="button"
          onClick={() => {
            void signOut();
            setOpen(false);
          }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
